/**
 * Helpers puros del look del masterplan CAD. Sin MapLibre ni React.
 * La API pública del tema sigue siendo `CAD_MASTERPLAN_STYLE` en mapTheme.js.
 */

/** Sol único: azimut geográfico (° desde el norte, horario) y elevación (°). */
export const CAD_MASTERPLAN_SUN = {
  azimuth: 350,
  elevation: 48,
};

/**
 * Materiales del loteo desacoplados del tema de UI.
 * Hex fijos que reproducen el look previo (ex-mixThemeHex sobre tokens).
 */
export const CAD_MASTERPLAN_MATERIALS = {
  site: '#C9C8C2',
  siteLine: '#7F8185',
  asphalt: '#595c5e',
  curb: '#a6a6a3',
  grass: '#416b30',
  canopy: '#3a5b2d',
  shadow: '#242424',
};

/** m/px en ecuador zoom 0 con tiles de 512: 156543.0339 / 2. */
const METERS_PER_PX_Z0_512 = 78271.517;

/**
 * Metros por píxel a una latitud y zoom (tiles 512).
 *
 * @param {number} lat
 * @param {number} zoom
 * @returns {number}
 */
export function metersPerPixel(lat, zoom) {
  const latRad = (Number(lat) || 0) * (Math.PI / 180);
  const z = Number(zoom);
  if (!Number.isFinite(z)) return Infinity;
  return METERS_PER_PX_Z0_512 * Math.cos(latRad) / (2 ** z);
}

/**
 * Expresión MapLibre `interpolate` zoom → píxeles para un ancho en metros.
 * Stops cada 0.5 de zoom. `['zoom']` solo como entrada del interpolate.
 *
 * @param {number} meters
 * @param {number} lat
 * @param {{ minPx?: number, maxPx?: number, zoom?: [number, number] }} [options]
 * @returns {Array}
 */
export function metric(meters, lat, options = {}) {
  const minPx = Number.isFinite(options.minPx) ? options.minPx : 0.5;
  const maxPx = Number.isFinite(options.maxPx) ? options.maxPx : 64;
  const zoomRange = Array.isArray(options.zoom) && options.zoom.length === 2
    ? options.zoom
    : [13, 20];
  const z0 = Number(zoomRange[0]);
  const z1 = Number(zoomRange[1]);
  const expr = ['interpolate', ['linear'], ['zoom']];
  const m = Math.abs(Number(meters) || 0);
  const i0 = Math.round(z0 * 2);
  const i1 = Math.round(z1 * 2);
  for (let i = i0; i <= i1; i += 1) {
    const z = i / 2;
    const mpp = metersPerPixel(lat, z);
    const raw = mpp > 0 && Number.isFinite(mpp) ? m / mpp : minPx;
    const px = Math.min(maxPx, Math.max(minPx, raw));
    expr.push(z, Number(px.toFixed(3)));
  }
  return expr;
}

/**
 * Escala los valores numéricos de un interpolate zoom (índices impares tras zoom).
 *
 * @param {Array} expr
 * @param {number} factor
 * @returns {Array|number}
 */
export function scaleZoomInterpolate(expr, factor) {
  const f = Number(factor);
  if (!Array.isArray(expr) || expr[0] !== 'interpolate') return expr;
  if (!Number.isFinite(f) || f === 1) return expr;
  if (f <= 0) return 0;
  const out = expr.slice();
  for (let i = 3; i < out.length; i += 2) {
    if (typeof out[i] === 'number') {
      out[i] = Number((out[i] * f).toFixed(3));
    }
  }
  return out;
}

/**
 * Traslación de sombra opuesta al sol. Con `translate-anchor: map`,
 * +x este, +y sur. Devuelve interpolate de `['literal', [dx, dy]]`.
 *
 * @param {number} heightM altura aparente del objeto
 * @param {{ azimuth: number, elevation: number }} sun
 * @param {number} lat
 * @param {{ minPx?: number, maxPx?: number, zoom?: [number, number] }} [options]
 * @returns {Array}
 */
export function metricShadowTranslate(heightM, sun, lat, options = {}) {
  const elevation = Math.max(1, Number(sun?.elevation) || 45);
  const lightAz = Number(sun?.azimuth) || 0;
  const shadowAzRad = ((lightAz + 180) * Math.PI) / 180;
  const lengthM = Math.abs(Number(heightM) || 0) / Math.tan((elevation * Math.PI) / 180);
  const lengthExpr = metric(lengthM, lat, options);
  const out = ['interpolate', ['linear'], ['zoom']];
  for (let i = 3; i < lengthExpr.length; i += 2) {
    const z = lengthExpr[i - 1];
    const px = lengthExpr[i];
    const dx = Math.sin(shadowAzRad) * px;
    const dy = -Math.cos(shadowAzRad) * px;
    out.push(z, ['literal', [Number(dx.toFixed(3)), Number(dy.toFixed(3))]]);
  }
  return out;
}

/**
 * Copas ~×2 por nivel de zoom (proporcional al mundo), con tope.
 *
 * @param {{ baseZoom?: number, baseSize?: number, endZoom?: number, maxSize?: number }} [options]
 * @returns {Array<[number, number]>}
 */
export function buildTreeIconSizeStops(options = {}) {
  const baseZoom = Number.isFinite(options.baseZoom) ? options.baseZoom : 14.5;
  const baseSize = Number.isFinite(options.baseSize) ? options.baseSize : 0.12;
  const endZoom = Number.isFinite(options.endZoom) ? options.endZoom : 19.5;
  const maxSize = Number.isFinite(options.maxSize) ? options.maxSize : 0.85;
  const stops = [];
  const i0 = Math.round(baseZoom * 2);
  const i1 = Math.round(endZoom * 2);
  for (let i = i0; i <= i1; i += 1) {
    const z = i / 2;
    const size = Math.min(maxSize, baseSize * (2 ** (z - baseZoom)));
    stops.push([z, Number(size.toFixed(4))]);
  }
  return stops;
}

/**
 * polar MapLibre = ángulo desde el cenit → elevation desde el horizonte.
 *
 * @param {{ azimuth: number, elevation: number }} sun
 * @param {{ radial?: number, intensity?: number }} [opts]
 * @returns {{ anchor: string, position: number[], intensity: number }}
 */
export function sunLightFromSun(sun, opts = {}) {
  const azimuth = Number(sun?.azimuth) || 0;
  const elevation = Math.min(89, Math.max(1, Number(sun?.elevation) || 45));
  const polar = 90 - elevation;
  return {
    anchor: 'map',
    position: [Number.isFinite(opts.radial) ? opts.radial : 1.2, azimuth, polar],
    intensity: Number.isFinite(opts.intensity) ? opts.intensity : 0.32,
  };
}

/**
 * @param {'high'|'mid'|'low'} quality
 * @returns {{ blurFactor: number, treeShadowOpacity: number }}
 */
function qualityProfile(quality) {
  if (quality === 'low') return { blurFactor: 0, treeShadowOpacity: 0 };
  if (quality === 'mid') return { blurFactor: 0.45, treeShadowOpacity: 0.12 };
  return { blurFactor: 1, treeShadowOpacity: 0.22 };
}

/**
 * Factory del tema visual del masterplan. Misma forma de claves que el
 * objeto estático previo; los consumidores no cambian.
 *
 * @param {{ lat?: number, quality?: 'high'|'mid'|'low' }} [options]
 * @returns {object}
 */
export function createCadMasterplanStyle({ lat = -30, quality = 'high' } = {}) {
  const M = CAD_MASTERPLAN_MATERIALS;
  const sun = CAD_MASTERPLAN_SUN;
  const q = qualityProfile(quality);

  const sidewalkWidth = metric(2.5, lat, { minPx: 1, maxPx: 28, zoom: [13, 20] });
  const gutterWidth = metric(0.25, lat, { minPx: 0.4, maxPx: 3, zoom: [13, 20] });
  const gutterLineOffset = metric(0.8, lat, { minPx: 0.5, maxPx: 6, zoom: [13, 20] });
  const contactShadowWidth = metric(4, lat, { minPx: 2, maxPx: 28, zoom: [13, 20] });
  const contactShadowBlur = scaleZoomInterpolate(
    metric(3.2, lat, { minPx: 1.5, maxPx: 22, zoom: [13, 20] }),
    q.blurFactor,
  );
  const contactShadowLineOffset = metric(1.5, lat, { minPx: 0.8, maxPx: 8, zoom: [13, 20] });

  return {
    siteFill: M.site,
    siteFillOpacity: 0.16,
    siteLine: M.siteLine,
    streetFill: M.asphalt,
    streetFillOpacity: 0.9,
    streetShadowFill: M.shadow,
    /**
     * @deprecated La sombra de contacto + light del mapa alcanzan; opacidad 0
     * para no ensuciar el asfalto. Se mantiene la clave por compatibilidad.
     */
    streetShadowOpacity: 0,
    streetShadowTranslate: metricShadowTranslate(0.45, sun, lat, {
      minPx: 0.4,
      maxPx: 4,
      zoom: [13, 20],
    }),
    sidewalkColor: M.curb,
    sidewalkOpacity: 0.88,
    sidewalkWidth,
    gutterColor: M.shadow,
    gutterOpacity: 0.28,
    gutterWidth,
    gutterLineOffset,
    contactShadowColor: M.shadow,
    contactShadowOpacity: 0.18,
    contactShadowWidth,
    contactShadowBlur,
    contactShadowLineOffset,
    sunLight: sunLightFromSun(sun),
    greenFill: M.grass,
    greenFillOpacity: 0.90,
    treeCanopy: M.canopy,
    treeCanopyOpacity: 0.94,
    treeShadow: M.shadow,
    treeShadowOpacity: q.treeShadowOpacity,
    treeStroke: M.shadow,
    treeMinZoom: 15,
    treeShadowTranslate: metricShadowTranslate(3.2, sun, lat, {
      minPx: 1,
      maxPx: 14,
      zoom: [14, 20],
    }),
    treeVariants: 4,
    treeIconSizeStops: buildTreeIconSizeStops(),
    treeIconSizeByTreeSize: { sm: 0.64, lg: 1.46, md: 1 },
  };
}
